/**
 * @fileoverview system-web file-service bootstrap.
 * @module system-web/services/fileServiceBootstrap
 *
 * Responsibilities: - Wire @firesquirrel/file-service-client to system-web's API client.
 * Dependencies:     - @firesquirrel/file-service-client, ./apiClient.
 * Architecture:     - Call initFileServiceResolver() once during app startup (main.tsx).
 *                   - System-web IS the system API, so both tenant and system file
 *                     resolution route through the same /api/files endpoint.
 * Notes:            - Idempotent — safe to call multiple times.
 */

import { initFileUrlResolver } from '@firesquirrel/file-service-client/client';
import { apiFetch } from '../apiClient';

let initialized = false;

/**
 * Initialize the singleton file URL resolver. Idempotent.
 * Must be called before any component using useUploadedFileUrl mounts.
 */
export function initFileServiceResolver(): void {
  if (initialized) return;
  initialized = true;

  initFileUrlResolver({
    // System-web's /api/files IS the system file endpoint.
    async getSystemFileDownloadUrl(fileId: string): Promise<string> {
      const data = await apiFetch(`/api/files/${encodeURIComponent(fileId)}/download`) as { downloadUrl?: string };
      if (!data.downloadUrl) throw new Error('No download URL returned');
      return data.downloadUrl;
    },

    // System-web doesn't have a tenant-api proxy; all files are system-scoped.
    // The tenant endpoint always throws so the resolver skips it and goes
    // straight to the system endpoint.
    async getTenantFileDownloadUrl(_fileId: string): Promise<string> {
      throw new Error('system-web has no tenant file endpoint');
    },
  });
}
